import React, { useState, useEffect } from 'react';
import { formatTicketStatus, formatPriority, formatDate, isTicketUnread } from '../utils/supportUtils';

const TicketList = ({ tickets, onTicketSelect, isAdmin = false, loading = false, user }) => {
  const [unreadTickets, setUnreadTickets] = useState(new Set());
  useEffect(() => {
    if (tickets && user) {
      checkUnreadStatus();
    }
  }, [tickets, user, isAdmin]);

  useEffect(() => {
    // Listen for ticketRead events to update unread status
    const handleTicketRead = () => {
      checkUnreadStatus();
    };

    window.addEventListener('ticketRead', handleTicketRead);
    
    return () => {
      window.removeEventListener('ticketRead', handleTicketRead);
    };
  }, [tickets, user, isAdmin]);

  const checkUnreadStatus = async () => {
    if (!tickets || !user) return;

    const unreadSet = new Set();
    
    // Check each ticket for unread status
    for (const ticket of tickets) {
      const isUnread = await isTicketUnread(ticket.id, user.id, isAdmin);
      if (isUnread) {
        unreadSet.add(ticket.id);
      }
    }
    
    setUnreadTickets(unreadSet);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="status-card">
          <div className="spinner" />
          <div>
            <h3>Loading tickets</h3>
            <p>Fetching the latest support activity...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <div className="card">
        <div className="text-center py-6">
          <svg className="mx-auto h-12 w-12 text-text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-lg font-medium text-text-light mb-2">No tickets found</h3>
          <p className="text-text-muted">
            {isAdmin 
              ? "No support tickets have been created yet." 
              : "You haven't created any support tickets yet."
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-2 py-2 md:px-3 md:py-3 border-b border-border-dark/60">
        <h2 className="text-lg font-semibold text-text-light">
          {isAdmin ? 'All Support Tickets' : 'Your Support Tickets'}
        </h2>
        <p className="text-sm text-text-muted mt-1">
          {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="divide-y divide-border-dark/40">
        {tickets.map((ticket) => {
          const statusInfo = formatTicketStatus(ticket.status);
          const priorityInfo = formatPriority(ticket.priority);
          const messageCount = ticket.ticket_messages?.length || 0;
          const attachmentCount = ticket.ticket_attachments?.length || 0;
          const lastMessage = ticket.ticket_messages?.[ticket.ticket_messages.length - 1];

          return (
            <div
              key={ticket.id}
              onClick={() => onTicketSelect(ticket)}
              className="p-4 md:p-5 hover:bg-white/5 cursor-pointer transition-colors rounded-xl"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Title and Status */}
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-base font-medium text-text-light truncate pr-4">
                      {ticket.title}
                    </h3>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityInfo.color}`}>
                        {priorityInfo.label}
                      </span>
                      {unreadTickets.has(ticket.id) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-300 border border-red-500/40">
                          Unread
                        </span>
                      )}
                    </div>
                  </div>

                  {/* User Email (for admin view) */}
                  {isAdmin && (
                    <div className="flex items-center mb-2">
                      <svg className="w-4 h-4 text-text-muted mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm text-text-muted">{ticket.user_email}</span>
                    </div>
                  )}

                  {/* Description Preview */}
                  <p className="text-sm text-text-muted mb-3 line-clamp-2">
                    {ticket.description}
                  </p>

                  {/* Metadata */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-xs text-text-muted">
                      <span>Created {formatDate(ticket.created_at)}</span>
                      {lastMessage && (
                        <span>Last reply {formatDate(lastMessage.created_at)}</span>
                      )}
                    </div>

                    <div className="flex items-center space-x-3 text-xs text-text-muted">
                      {messageCount > 0 && (
                        <div className="flex items-center space-x-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-2.319-.324l-4.596 1.836a.75.75 0 01-.977-.977l1.836-4.596A8.013 8.013 0 013 12a8 8 0 018-8c4.418 0 8 3.582 8 8z" />
                          </svg>
                          <span>{messageCount}</span>
                        </div>
                      )}
                      {attachmentCount > 0 && (
                        <div className="flex items-center space-x-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span>{attachmentCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Arrow Icon */}
                <div className="ml-4 flex-shrink-0">
                  <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TicketList;
